import { Component, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ExerciseSlot, ExerciseDbService, ExerciseTextLanguage } from 'services/exercise-db.service';
import { AuthService } from 'services/auth.service';
import { AuthRole } from '@cf-shared/couch-auth';
import { CsPage, NavController } from 'providers/nav-controller';
import { IAdminEditExerciseSlotPage } from 'pages/admin-edit-exercise-slot/admin-edit-exercise-slot-interface';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { DocWatcher } from 'services/database-wrapper';

@Component({
    selector: 'page-admin-exercise-slot-list',
    templateUrl: 'admin-exercise-slot-list.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ModalPageMarker]
})
export class AdminExerciseSlotListPage extends CsPage<{}> {
  protected slots: ExerciseSlot[] = [];
  protected filteredSlots: ExerciseSlot[] = [];
  slotErrors: string[] = [];
  searchText: string = '';

  constructor(
    public navCtrl: NavController,
    protected exerciseDb: ExerciseDbService,
    protected auth: AuthService,
    private cdRef: ChangeDetectorRef,
  ) {
    super();
  }

  async ionViewCanEnter() {
    await this.auth.pageAuthGuardAsync(AuthRole.Dev);
  }
  async ionViewWillEnter() {
    // withDocWatcherAsync, not a hand-written create/destroy: every read below can throw when
    // the database could not be read at all, and a `destroy()` after them would never run,
    // leaving this watcher registered for the rest of the session.
    await this.exerciseDb.database.withDocWatcherAsync(watcher => this.loadSlotsAsync(watcher));
    this.applyFilter();
    this.cdRef.markForCheck();
  }

  // Read the slots and work out which exercises appear in more than one of them. Split out of
  // ionViewWillEnter only so withDocWatcherAsync can own the watcher's lifetime. It does NOT
  // own that watcher: the caller supplies a live one and is responsible for destroying it, so
  // this must not be called on its own.
  private async loadSlotsAsync(watcher: DocWatcher) {
    this.slots = await this.exerciseDb.getAllSlotsAsync(watcher);

    // check that one exercise appears in only one slot.
    //
    // Keyed by exercise ID, never by name. Names are localized now (#4210), and
    // a name is not an identity: two different exercises whose English names
    // differ can share a translation, which would report a false duplicate to
    // one admin and not another. The name is read separately, pinned to the
    // AUTHORING locale, so the message reads the same for every admin.
    const slotsByExerciseId = new Map<string, ExerciseSlot[]>();
    this.slotErrors = [];
    for (const slot of this.slots) {
      for (const ese of slot.exercises) {
        if (!slotsByExerciseId.has(ese.exerciseId)) {
          slotsByExerciseId.set(ese.exerciseId, []);
        }
        slotsByExerciseId.get(ese.exerciseId).push(slot);
      }
    }
    for (const [exerciseId, slots] of slotsByExerciseId) {
      if (slots.length > 1) {
        const exerciseName = await this.exerciseDb.getExerciseNameByIdAsync(
          exerciseId, watcher, ExerciseTextLanguage.Authoring);
        this.slotErrors.push(`Exercise ${exerciseName} appears in multiple slots: ${slots.map(s => s.slotName).join(', ')}`);
      }
    }
  }

  handleSearchChanged() {
    this.applyFilter();
    this.cdRef.markForCheck();
  }

  applyFilter() {
    const searchLower = this.searchText.toLowerCase().trim();
    if (!searchLower) {
      this.filteredSlots = this.slots;
    } else {
      this.filteredSlots = this.slots.filter(s =>
        s.slotName.toLowerCase().includes(searchLower)
      );
    }
  }

  editSlot(slot: ExerciseSlot) {
    void this.navCtrl.pushAsync(IAdminEditExerciseSlotPage, { id: slot._id });
  }
  newSlot() {
    void this.navCtrl.pushAsync(IAdminEditExerciseSlotPage, { id: '' });
  }
}
